import {
  EBluetoothCharacteristicType,
  EDatasourceType,
  ESdChangeReason,
  IAction,
  IActionDefinitionDto,
  ICharacteristicFunctionInput
} from "@kuika/kuika-cl-model";
import { Form, FormInstance } from "antd/es";
import * as React from "react";
import { DatasourceService } from "../../../../services/datasource-service";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import { PureSdComponent } from "../../../../shared/pure-sd";
import { KMainFunctions } from "../../../../shared/utility/main-functions";
import { SaveStatus } from "../../../../shared/utility/save-status";
import { DefaultModal } from "../../../common/default-modal";
import { InputTooltip } from "../../../common/input-tooltip";
import KuikaButton, { type KuikaButtonStatus } from "../../../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../../../ui-kit/kuika-input/kuika-input";
import KuikaSelectbox, { KuikaSelectboxOption } from "../../../ui-kit/kuika-selectbox/kuika-selectbox";

// The four ways a characteristic can be driven, in the order the BLE spec lists them.
const CHARACTERISTIC_TYPE_OPTIONS: KuikaSelectboxOption[] = [
  { value: EBluetoothCharacteristicType.Read, label: "Read" },
  { value: EBluetoothCharacteristicType.Write, label: "Write" },
  { value: EBluetoothCharacteristicType.Notify, label: "Notify" },
  { value: EBluetoothCharacteristicType.Indicate, label: "Indicate" }
];

export interface ICreateMethodState {
  /** Where the save stands. Owned by `SaveStatus`, never set directly. */
  saveStatus: KuikaButtonStatus;
}

export interface ICreateMethodProps {
  editingDsId: string;
  editingMethodId?: string;
  handleCreateMethod: any;
  showModal: boolean;
  existingMethods: IActionDefinitionDto[];
  closeModal: () => void;
}

class CreateMethod extends PureSdComponent<ICreateMethodProps & IWithSDProps, ICreateMethodState> {
  formRef = React.createRef<FormInstance>();

  methodActions: IAction[] = [];

  methodInputs: ICharacteristicFunctionInput[] = [];

  private readonly saveStatus: SaveStatus = new SaveStatus((saveStatus: KuikaButtonStatus): void => {
    this.setState({ saveStatus });
  });

  constructor(props: ICreateMethodProps & IWithSDProps) {
    super(props);
    this.state = {
      saveStatus: "idle"
    };
  }

  componentWillUnmount(): void {
    this.saveStatus.dispose();
  }

  componentDidUpdate(prevProps: ICreateMethodProps & IWithSDProps) {
    if (
      (this.props.editingMethodId === undefined && prevProps.editingMethodId != undefined) ||
      (this.props.sd.editingApiMethodId === undefined && prevProps.sd.editingApiMethodId != undefined)
    ) {
      this.formRef.current?.resetFields();
      this.methodActions = [];
      this.methodInputs = [];
      this.saveStatus.reset();
    } else if (this.props.editingMethodId != undefined && prevProps.editingMethodId === undefined) {
      this.loadConfig();
    } else if (this.props.editingMethodId != undefined && prevProps.editingMethodId != undefined) {
      if (this.props.editingMethodId !== prevProps.editingMethodId) {
        this.loadConfig();
      }
    }
  }

  componentDidMount = () => {
    this.loadConfig();
  };

  loadConfig = () => {
    const method = this.props.sd.actionDefinitions.find((a) => a.id === this.props.editingMethodId);
    if (method && method.bluetoothInfo) {
      const bluetoothInfo = JSON.parse(method.bluetoothInfo as unknown as string);
      this.formRef.current?.resetFields();
      const fieldsValue = this.formRef.current?.getFieldsValue();
      fieldsValue.id = bluetoothInfo.id;
      fieldsValue.functionName = bluetoothInfo.functionName;
      fieldsValue.serviceId = bluetoothInfo.serviceId;
      fieldsValue.characteristicType = bluetoothInfo.characteristicType;
      this.formRef.current?.setFieldsValue(fieldsValue);
      this.methodActions = bluetoothInfo.actions;
      this.methodInputs = bluetoothInfo.inputs;
    }
  };

  onSubmit = (data: any) => {
    // The commit is live again as soon as the ✓ replaces the spinner, so a second press would
    // re-send the save behind the closing dialog.
    if (!this.saveStatus.canStart) return;
    this.saveStatus.start();

    const action = {
      kDatasource_Id: this.props.editingDsId,
      id: this.props.editingMethodId,
      name: data.functionName,
      actionType: EDatasourceType.BleService,
      parameters: [],
      bluetoothInfo: JSON.stringify({ ...data, actions: this.methodActions, inputs: this.methodInputs })
    } as unknown as IActionDefinitionDto;

    DatasourceService.SaveAction(action)
      .then((response): void => {
        if (response.status !== 200) {
          // A resolved response that is not 200: the `finally` reset used to rescue this branch.
          this.saveStatus.fail();
          return;
        }
        this.props.sd.refreshActions();
        // The ✓ goes up first and the dialog leaves after it — see `SaveStatus`. The saved
        // characteristic is then the active one in the list behind, so there is no toast to add.
        this.saveStatus.succeed((): void => {
          this.props.sd.setActiveBluetoothMethod(action.id);
          this.props.handleCreateMethod();
          this.props.sd.setEditingBluetoothMethod(undefined);
          this.props.closeModal();
        });
      })
      .catch((e): void => {
        this.saveStatus.fail();
        KMainFunctions.exceptionHandler(e);
      });
  };

  render() {
    const isMainAI = KMainFunctions.isMainAI();
    return (
      <>
        <DefaultModal
          showModal={this.props.showModal}
          closeModal={this.props.closeModal}
          closeSecondModal={this.props.closeModal}
          firstHeader={`${this.props.editingMethodId != undefined ? "Edit" : "New"} Method`}
          className="config-manager__modal"
          width={600}
          centered={true}
          bodyStyle={{ padding: 0 }}
        >
          <Form name="createMethod" ref={this.formRef} onFinish={this.onSubmit} style={{ height: "100%" }}>
            <div
              className="kuika_app-settings__content__container kuika_app-settings__content__container__passwords"
              style={{ padding: 24 }}
            >
              <div>
                <div className="property__topic-text" style={{ marginBottom: 24 }}>
                  To access the data provided by the device you added, you need to define a method. You need to provide
                  information for the method definition process.
                  {!isMainAI && (
                    <a
                      href="https://academy.kuika.com/en/content/adding-a-bluetooth-data-source-user-guide"
                      target="_blank"
                      rel="noreferrer"
                    >
                      {" "}
                      Learn more
                    </a>
                  )}
                </div>

                <div className="passwords__property">
                  <span className="property__text">
                    Name
                    <span className="property__required">*</span>
                    <InputTooltip
                      description="You should name the method you are defining."
                      helpLink="https://academy.kuika.com/en/content/adding-a-bluetooth-data-source-user-guide"
                    />
                  </span>
                  <Form.Item
                    name="functionName"
                    style={{ width: 380 }}
                    rules={[
                      { required: true, message: "Please enter a Characteristic Name." },
                      { pattern: /^[A-Za-z_][A-Za-z0-9_]*$/, message: "Please enter a valid Characteristic Name." },
                      {
                        pattern: /^(?!.*_bluetooth$).*/,
                        message: "This name is reserved. Please enter another Characteristic Name"
                      }
                    ]}
                  >
                    <KuikaInput autoFocus autoComplete="off" style={{ width: "100%" }} />
                  </Form.Item>
                </div>
                <div className="passwords__property">
                  <span className="property__text">
                    Characteristic Id
                    <span className="property__required">*</span>
                    <InputTooltip
                      description="Enter the Characteristic Id information of the device you are defining the method for and have connected via Bluetooth."
                      helpLink="https://academy.kuika.com/en/content/adding-a-bluetooth-data-source-user-guide"
                    />
                  </span>
                  <Form.Item
                    name="id"
                    style={{ width: 380 }}
                    rules={[
                      { required: true, message: "Please enter a Characteristic ID." },
                      { pattern: /^\S*$/, message: "Please enter a valid Characteristic ID" }
                    ]}
                  >
                    <KuikaInput autoFocus autoComplete="off" style={{ width: "100%" }} />
                  </Form.Item>
                </div>
                <div className="passwords__property">
                  <span className="property__text">
                    Service ID
                    <InputTooltip
                      description="If there are IDs under different characteristics, you can enter the Service Id information to define which characteristic under which service you will use."
                      helpLink="https://academy.kuika.com/en/content/adding-a-bluetooth-data-source-user-guide"
                    />
                  </span>
                  <Form.Item name="serviceId" style={{ width: 380 }}>
                    <KuikaInput autoFocus autoComplete="off" style={{ width: "100%" }} />
                  </Form.Item>
                </div>
                <div className="passwords__property">
                  <span className="property__text">
                    Characteristic Type
                    <span className="property__required">*</span>
                    <InputTooltip
                      description="While defining the method, you can determine how the characteristic will work using the Characteristic Type."
                      helpLink="https://academy.kuika.com/en/content/adding-a-bluetooth-data-source-user-guide"
                    />
                  </span>
                  <Form.Item
                    name="characteristicType"
                    style={{ width: 380 }}
                    rules={[{ required: true, message: "Please enter a Characteristic Type." }]}
                  >
                    <KuikaSelectbox options={CHARACTERISTIC_TYPE_OPTIONS} style={{ width: "100%" }} />
                  </Form.Item>
                </div>
              </div>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                padding: 24,
                borderTop: "1px solid var(--dark-3)"
              }}
            >
              <div></div>
              <div style={{ display: "flex" }}>
                <KuikaButton htmlType="submit" type="primary" size="md" uppercase status={this.state.saveStatus}>
                  Save
                </KuikaButton>
              </div>
            </div>
          </Form>
        </DefaultModal>
      </>
    );
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    return false;
  };
}

const createMethod = withSD<ICreateMethodProps>(CreateMethod);
export { createMethod as CreateMethod };
